import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { EditableTextField } from '~/components/Shared/EditableTextField/EditableTextField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { api } from '~/redux/api/createApi';
import { ERROR, SUCCESS } from '~/types/toast.types';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';

type FieldType = 'instructions' | 'fallbackResponse';

export const GeneralRoutingTab = () => {
  const intl = useIntl();

  const [instructions, setInstructions] = useState<string>('');
  const [fallbackResponse, setFallbackResponse] = useState<string>('');

  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [isEditingFallback, setIsEditingFallback] = useState(false);

  const {
    data: routingConfig,
    isFetching,
    error,
  } = api.endpoints.getRoutingConfiguration.useQuery(undefined);

  const [patchRoutingConfiguration, { isLoading: isSaving }] =
    api.endpoints.patchRoutingConfiguration.useMutation();

  useEffect(() => {
    if (routingConfig) {
      setInstructions(routingConfig.instructions || '');
      setFallbackResponse(routingConfig.fallbackResponse || '');
    }
  }, [routingConfig]);

  const handleSave = useCallback(
    async (field: FieldType) => {
      const fieldValueMap: Record<FieldType, string> = {
        instructions,
        fallbackResponse,
      };

      try {
        await patchRoutingConfiguration({
          configInput: {
            [field]: fieldValueMap[field],
            versionNumber: routingConfig?.versionNumber ?? 0,
          },
        }).unwrap();

        if (field === 'instructions') {
          setIsEditingInstructions(false);
        } else {
          setIsEditingFallback(false);
        }

        showToast(
          SUCCESS,
          intl.formatMessage({
            id: `GeneralRoutingTab.${field}SaveSuccess`,
            defaultMessage:
              field === 'instructions'
                ? 'General routing instructions saved successfully.'
                : 'Fallback response saved successfully.',
            description: `Success message when ${field} is saved`,
          }),
        );
      } catch {
        showToast(
          ERROR,
          intl.formatMessage({
            id: `GeneralRoutingTab.${field}SaveError`,
            defaultMessage:
              field === 'instructions'
                ? 'Failed to save instructions. Please try again.'
                : 'Failed to save fallback response. Please try again.',
            description: `Error message when ${field} save fails`,
          }),
        );
      }
    },
    [
      patchRoutingConfiguration,
      instructions,
      fallbackResponse,
      intl,
      routingConfig,
    ],
  );

  const handleCancel = useCallback(
    (field: FieldType) => {
      if (field === 'instructions') {
        setInstructions(routingConfig?.instructions || '');
        setIsEditingInstructions(false);
      } else {
        setFallbackResponse(routingConfig?.fallbackResponse || '');
        setIsEditingFallback(false);
      }
    },
    [routingConfig],
  );

  if (isFetching && !routingConfig) {
    return <LoadingContainer isLoading />;
  }

  if (error) {
    return (
      <Layout align="center" justify="center" p={8}>
        <Layout direction="column" align="center" spacing={4}>
          <Text variant="title">
            {intl.formatMessage({
              id: 'GeneralRoutingTab.errorHeading',
              defaultMessage: 'Something went wrong',
              description: 'Error state heading',
            })}
          </Text>
          <Text variant="body">
            {intl.formatMessage({
              id: 'GeneralRoutingTab.errorDescription',
              defaultMessage:
                'Unable to load routing configuration. Please try again.',
              description: 'Error state description',
            })}
          </Text>
        </Layout>
      </Layout>
    );
  }

  return (
    <Layout direction="column" spacing={4} p={6} maxW="800px">
      <EditableTextField
        title={intl.formatMessage({
          id: 'GeneralRoutingTab.instructionsTitle',
          defaultMessage: 'General Routing Instructions',
          description: 'Title for general routing instructions',
        })}
        subtitle={intl.formatMessage({
          id: 'GeneralRoutingTab.instructionsSubtitle',
          defaultMessage:
            'This instruction will be shown in the welcome message of the workflow routing.',
          description: 'Subtitle for general routing instructions',
        })}
        value={instructions}
        placeholder={intl.formatMessage({
          id: 'GeneralRoutingTab.instructionsPlaceholder',
          defaultMessage:
            "e.g. I'm here to help you find the right place to log your needs.\nTo get started, please tell me a bit about your legal request. You can describe the type of the request (e.g., to create a contract, for litigation support, or an intellectual property filing), the purpose of the legal request, or any specific parties involved. \nI will ask a few questions to guide you. If you get stuck or want to start over, just type 'help' or 'start over'.",
          description: 'Placeholder for instructions textarea',
        })}
        maxLength={200}
        isEditing={isEditingInstructions}
        isSaving={isSaving}
        onEdit={() => setIsEditingInstructions(true)}
        onSave={() => handleSave('instructions')}
        onCancel={() => handleCancel('instructions')}
        onChange={setInstructions}
        optionalTooltip={intl.formatMessage({
          id: 'GeneralRoutingTab.instructionsTooltip',
          defaultMessage:
            "This field holds the agent's welcome message and initial guidance. The text appears as the very first message the user sees, helping them understand the agent's purpose and how to get started.",
          description: 'Tooltip for general instructions optional label',
        })}
      />

      <EditableTextField
        title={intl.formatMessage({
          id: 'GeneralRoutingTab.fallbackTitle',
          defaultMessage: 'Fallback Response',
          description: 'Title for fallback response',
        })}
        subtitle={intl.formatMessage({
          id: 'GeneralRoutingTab.fallbackSubtitle',
          defaultMessage:
            'This is the response displayed if the agent cannot find a relevant workflow.',
          description: 'Subtitle for fallback response',
        })}
        value={fallbackResponse}
        placeholder={intl.formatMessage({
          id: 'GeneralRoutingTab.fallbackPlaceholder',
          defaultMessage:
            "I'm sorry but I could not find a workflow that matches your request. The information you provided may not be specific enough for me to make a recommendation.\nPlease try rephrasing your request with more details about the contract's type, purpose, or scope of work.",
          description: 'Placeholder for fallback response textarea',
        })}
        maxLength={1000}
        isEditing={isEditingFallback}
        isSaving={isSaving}
        onEdit={() => setIsEditingFallback(true)}
        onSave={() => handleSave('fallbackResponse')}
        onCancel={() => handleCancel('fallbackResponse')}
        onChange={setFallbackResponse}
        optionalTooltip={intl.formatMessage({
          id: 'GeneralRoutingTab.fallbackTooltip',
          defaultMessage:
            'A good fallback response acknowledges the issue and then offers a clear path forward, such as a link to a general support page or a prompt to contact the help.',
          description: 'Tooltip for fallback response optional label',
        })}
      />
    </Layout>
  );
};
